import React, { useState, useEffect } from 'react';
import { showNotification } from "../../redux/slices/notificationSlice";
import { useDispatch } from 'react-redux';
import CloudinaryFileUploader from '../Common/CloudinaryFileUploader';
import api from '../../axios/api';
import {
  CardContent,
  Typography,
  Box,
  TextField,
  Switch,
  FormControlLabel,
} from '@mui/material';
import { useForm, Controller } from 'react-hook-form';
import PageHeader from '../../componentsII/PageHeader';
import CustomButton from '../../componentsII/Buttons';

const Scope3ReviewModal = ({
  data: initialData,
  refetch,
  handleCloseMainDetailModal,
}) => {
  // Update your useForm to match the property names in initialData
  const { control, handleSubmit, setValue, watch, formState: { errors } } = useForm({
    defaultValues: {
      id: initialData?.id || '',
      selectedOption: '',
      file: {
        fileName: initialData?.review_file_name || '',
        filePath: initialData?.review_file_path || ''
      },
      tCO2e: initialData?.co2_emission_tonnes || '',
      completeReview: initialData?.review_done || false
    }
  });

  const [modalData, setModalData] = useState({
    id: initialData?.id || '',
    fileName: initialData?.review_file_name || '',
    filePath: initialData?.review_file_path || '',
    tCO2e: initialData?.co2_emission_tonnes || '',
    completeReview: initialData?.review_done || false
  });
  const dispatch = useDispatch();


  useEffect(() => {
    // Pre-fill form with initial data
    if (initialData) {
      setValue('tCO2e', initialData.co2_emission_tonnes || '');
      setValue('completeReview', initialData.review_done || false);
      setValue('id', initialData.id || '');

      if (initialData.review_file_name && initialData.review_file_path) {
        setValue('file', {
          name: initialData.review_file_name,
          path: initialData.review_file_path
        });
      }
    }
  }, [initialData, setValue]);

  const onSubmit = async (data) => {
    try {
      // Validate tCO2e field
      if (data.tCO2e === undefined || data.tCO2e === null || data.tCO2e === '') {
        dispatch(showNotification({
          message: 'Please enter a valid tCO2e value',
          type: 'error'
        }));
        return;
      }

      if(data.tCO2e <= 0){
        dispatch(showNotification({
          message: 'tCO2e value must be greater than 0',
          type: 'error'
        }));
        return;
      }

      // Validate file if it has been uploaded
      if (data.file && (typeof data.file !== 'object' || data.file === "null" ||
        !data.file.name || data.file.name === "null" ||
        !data.file.path || data.file.path === "null")) {
        dispatch(showNotification({
          message: 'Please upload a valid file',
          type: 'error'
        }));
        return;
      }

      // Create form data object to send
      const formData = new FormData();

      // Add id from initial data if it exists
      if (initialData && initialData.id) {
        formData.append('id', initialData.id);
      }

      // Add form data
      formData.append('tCO2e', data.tCO2e);
      formData.append('review_completed', data.completeReview ? 1 : 0);

      // Add file information if available
      if (data.file) {
        formData.append('file_name', data.file.name);
        formData.append('file_path', data.file.path);
      }

      // Make API call
      const response = await api.post('/admin/updateScope3Review', formData, {
        headers: {
          'Content-Type': 'application/json'
        }
      });

      // Show success notification
      dispatch(showNotification({
        message: "Scope3 data saved successfully!",
        type: "success"
      }));

      // Refresh data and close modal
      refetch();
      handleCloseMainDetailModal();
    } catch (error) {
      // Handle error
      dispatch(showNotification({
        message: error.response?.data?.message || "Error saving Scope3 data",
        type: "error"
      }));
      console.error('Submission error:', error);
    }
  };

  // Function to download existing file if available
  const handleDownloadFile = () => {
    if (modalData.filePath) {
      const forceDownload = (url, filename) => {
        fetch(url)
          .then(response => response.blob())
          .then(blob => {
            const blobUrl = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = blobUrl;
            link.setAttribute('download', filename || 'review_file.xlsx');
            document.body.appendChild(link);
            link.click();
            link.remove();
            URL.revokeObjectURL(blobUrl);
          })
          .catch(error => {
            console.error("Error downloading file:", error);
            dispatch(showNotification({ message: 'Download failed', type: 'error' }));
          });
      };

      forceDownload(modalData.filePath, modalData.fileName);
    } else {
      dispatch(showNotification({ message: 'No file available to download', type: 'warning' }));
    }
  };

  return (
    <div className="h-full">
      <h1 className="text-2xl font-bold text-green-600">Review File</h1>

      <CardContent className="space-y-4 p-4">
        {/* tCO2e Emission */}
        <Controller
          name="tCO2e"
          control={control}
          rules={{ required: "tCO2e is required" }}
          render={({ field }) => (
            <TextField
              {...field}
              size="small"
              label="tCO2e Emission"
              variant="outlined"
              fullWidth
              type="number"
              onChange={(e) => {
                const value = e.target.value;
                if (value === '' || /^[0-9]*\.?[0-9]*$/.test(value)) {
                  field.onChange(value);
                }
              }}
              error={!!errors.tCO2e}
              helperText={errors.tCO2e?.message}
            />
          )}
        />

        {/* File Upload */}
        <Box>
          <Typography variant="subtitle1" className="mb-2">
            Upload File
          </Typography>
          <Controller
            name="file"
            control={control}
            render={({ field: { onChange } }) => (
              <CloudinaryFileUploader
                multiple={false}
                allowedTypes="xlsx,xls"
                onUploadComplete={(fileResponse) => {
                  setValue('file', fileResponse);
                }}
                existingFiles={modalData && modalData.fileName ? [{
                  name: modalData.fileName,
                  path: modalData.filePath,
                  filename: modalData.fileName,
                  size: 0
                }] : []}
              />
            )}
          />
        </Box>

        {/* Complete Review Switch */}
        <Box>
          <Controller
            name="completeReview"
            control={control}
            render={({ field }) => (
              <FormControlLabel
                control={
                  <Switch
                    {...field}
                    checked={field.value}
                    onChange={(e) => field.onChange(e.target.checked)}
                  />
                }
                label="Mark as Complete Review"
              />
            )}
          />
        </Box>
      </CardContent>

      {/* Buttons */}
      <div className="flex justify-end gap-4 px-2">
        <CustomButton
          click={handleCloseMainDetailModal}
          className="bg-red-600 hover:bg-red-700 text-white"
          text="Cancel"
        />
        <CustomButton
          click={handleSubmit(onSubmit)}
          className="bg-green-600 hover:bg-green-700 text-white"
          text="Save"
        />
      </div>
    </div>
  );
};

export default Scope3ReviewModal;